import z from "zod";
import { ProjectBlockerReasonSchema } from "rdx/modules/projectBlockers/schemas";
import { LeadKindEnum } from "rdx/modules/leads/enums";
import { AuroraFinanceProductTypeEnum } from "rdx/modules/fundingProposals/enums";
import {
  PreSalesMilestoneNameEnum,
  StageNameEnum,
  RoofingNonrequestableReasonEnum,
  ProposalNonrequestableReasonEnum,
  AuroraNonrequestableReasonEnum,
  VisionNonrequestableReasonEnum,
  SoloNonrequestableReasonEnum,
  LightreachNonrequestableReasonEnum,
  ProjectTypeEnum,
} from "rdx/modules/projects/enums";
import { ProjectUserSchema } from "../base";
import { CrmActivityTypeSchema } from "./activityLog";

export const PreSalesIndexCrmActivitySchema = z.object({
  id: z.number(),
  leadId: z.number(),
  kind: CrmActivityTypeSchema,
  title: z.string(),
  meetingType: z.number().nullable(),
  meetingInfo: z.string(),
  location: z.string().nullable(),
  email: z.string(),
  due: z.string(),
  reminder: z.string(),
  reminderType: z.array(z.string()),
  notes: z.string(),
});

export const CrmActivitySchema = PreSalesIndexCrmActivitySchema.extend({
  completeType: z.string().nullable(),
  completedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
  archivedAt: z.string().nullable(),
  leadInfo: z.object({
    id: z.number(),
    address: z.object({ city: z.string(), state: z.string() }),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
  }),
  canDelete: z.boolean().optional(),
  reason: ProjectBlockerReasonSchema.optional(),
});

export const AuroraFinanceOptionsSchema = z.object({
  productTypes: z.array(z.nativeEnum(AuroraFinanceProductTypeEnum)),
  financeProducts: z.array(
    z.object({
      id: z.number(),
      productLabel: z.string(),
      productType: z.nativeEnum(AuroraFinanceProductTypeEnum),
      tooltipText: z.string().nullable(),
      productTypes: z.array(z.nativeEnum(AuroraFinanceProductTypeEnum)),
      options: z.object({
        id: z.number(),
        proposalFinanceProductId: z.number(),
        stateAbbr: z.string(),
        archivedAt: z.string().nullable(),
        escalators: z.array(z.string()),
        loanLength: z.array(z.string()),
        interestRate: z.array(z.string()),
        createdAt: z.string(),
        updatedAt: z.string(),
        productDescription: z.string().nullable(),
      }),
    }),
  ),
});

export const LeadOfferSchema = z.object({
  id: z.number(),
  projectId: z.number(),
  offererId: z.number(),
  receiverId: z.number(),
  percentage: z.string(),
  responseId: z.number().nullable(),
  rescindedAt: z.string().nullable(),
  rescindedById: z.number().nullable(),
  viewedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  negativeResponse: z.boolean(),
  lockInId: z.number().nullable(),
  enterpriseId: z.number(),
});

export const LatestProposalSchema = z.object({
  id: z.number(),
  projectId: z.number(),
  rejectedAt: z.string().nullable(),
  rejecterId: z.number().nullable(),
  revisionCompletedAt: z.string().nullable(),
  revisionDenialReason: z.string().nullable(),
  updatedAt: z.string(),
  url: z.string().nullable(),
  version: z.number(),
  createdAt: z.string(),
});

export const PreSalesProjectIndexSchema = z.object({
  id: z.string(),
  closeAt: z.string().optional(),
  leadId: z.number(),
  leadOwnerId: z.number(),
  leadFirstName: z.string(),
  leadLastName: z.string(),
  leadEmail: z.string(),
  leadPhone: z.string(),
  leadKind: z.nativeEnum(LeadKindEnum),
  leadReferrerId: z.number().nullable(),
  leadEnterpriseId: z.number().nullable(),
  siteId: z.number(),
  addressId: z.number(),
  addressCity: z.string(),
  addressState: z.string(),
  projectId: z.number().nullable(),
  projectSellerId: z.number(),
  projectMentorId: z.number().nullable(),
  projectManagerId: z.number().nullable(),
  coalescedUpdatedAt: z.string(),
  tpId: z.number(),
  tpCreatedAt: z.string(),
  tpUpdatedAt: z.string(),
  tpLatestStageAt: z.string(),
  projectStageId: z.number(),
  projectStageKind: z.number(),
  projectStageMilestoneName: z.nativeEnum(PreSalesMilestoneNameEnum),
  projectStageName: z.nativeEnum(StageNameEnum),
  projectStageScore: z.number(),
  leadQualificationPercent: z.number(),
  qualifyAt: z.string().nullable(),
  homeowner: z.object({
    leadId: z.number(),
    leadFirstName: z.string(),
    leadLastName: z.string(),
    leadEmail: z.string(),
    leadPhone: z.string(),
    leadCreatedAt: z.string(),
    address: z.object({
      addressStreet: z.string().nullable(),
      addressUnit: z.string().nullable(),
      addressCity: z.string().nullable(),
      addressState: z.string(),
      addressZip: z.string(),
      addressCountry: z.string(),
    }),
  }),
  enterpriseId: z.number().optional(),
  entOwnerId: z.number().optional(),
  offerable: z.boolean(),
  offer: LeadOfferSchema.optional(),
  tickets: z.number(),
  passedStageNames: z.array(z.nativeEnum(StageNameEnum)),
  upcomingStageNames: z.array(z.nativeEnum(StageNameEnum)),
  activities: z.array(PreSalesIndexCrmActivitySchema),
  isValidServiceArea: z.boolean(),
  roofingNonRequestableReason: z.nativeEnum(RoofingNonrequestableReasonEnum).nullable(),
  proposalNonrequestableReason: z.nativeEnum(ProposalNonrequestableReasonEnum).nullable(),
  auroraNonRequestableReason: z.nativeEnum(AuroraNonrequestableReasonEnum).nullable(),
  visionProposalNonrequestableReason: z.nativeEnum(VisionNonrequestableReasonEnum).nullable(),
  soloProposalNonrequestableReason: z.nativeEnum(SoloNonrequestableReasonEnum),
  lightreachProposalNonrequestableReason: z.nativeEnum(LightreachNonrequestableReasonEnum),
  requiresRsm: z.boolean(),
  canRequestTpoPricing: z.boolean(),
  auroraFinanceOptions: AuroraFinanceOptionsSchema,
  hasProposals: z.boolean(),
  isFastPayPreferred: z.boolean(),
  perWattStrategyId: z.number().nullish(),
  proposalUrl: z.string().nullable(),
  latestProposal: LatestProposalSchema.optional(),
  latestProposalRejection: z.string().nullable(),
  contractOut: z.string().nullable(),
  projectType: z.nativeEnum(ProjectTypeEnum),
  milestoneDisplayName: z.nativeEnum(PreSalesMilestoneNameEnum),
  projectStatus: z.object({
    name: z.string().optional(),
    display: z.string().optional(),
    reason: z.string().optional(),
  }),
  projectUsers: z.array(ProjectUserSchema),
  hasCurrentOffer: z.boolean(),
  currentOfferId: z.number().nullable(),
  hasExistingSystem: z.boolean().nullable(),
  isUserLeadGen: z.boolean(),
  visionProposal: z
    .object({
      id: z.string(),
      slug: z.string(),
    })
    .optional(),
});

export const FormattedPreSalesProjectIndexSchema = z.object({
  activities: z.array(PreSalesIndexCrmActivitySchema),
  address: z.object({
    id: z.number(),
    street: z.string().nullable(),
    unit: z.string().nullable(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
  }),
  auroraFinanceOptions: AuroraFinanceOptionsSchema,
  auroraNonRequestableReason: z.nativeEnum(AuroraNonrequestableReasonEnum).nullable(),
  canRequestTpoPricing: z.boolean(),
  updatedAt: z.string(),
  contractOut: z.string().nullable(),
  currentOfferId: z.number().nullable(),
  hasCurrentOffer: z.boolean(),
  hasExistingSystem: z.boolean().nullable(),
  hasProposals: z.boolean(),
  homeowner: z.object({
    firstName: z.string(),
    lastName: z.string(),
    qualificationPercent: z.number(),
    address: z.object({
      id: z.number(),
      street: z.string().nullable(),
      unit: z.string().nullable(),
      city: z.string(),
      state: z.string(),
      zip: z.string(),
    }),
  }),
  id: z.number(),
  isFastPayPreferred: z.boolean(),
  isUserLeadGen: z.boolean(),
  isValidServiceArea: z.boolean(),
  latestProposalRejection: z.string().nullable(),
  latestProposal: LatestProposalSchema.nullable(),
  lead: z.object({
    id: z.number(),
    email: z.string(),
    phone: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    fullName: z.string(),
    kind: z.nativeEnum(LeadKindEnum),
    referrerId: z.number().nullable(),
    userId: z.number(),
    address: z.object({
      id: z.number(),
      street: z.string().nullable(),
      unit: z.string().nullable(),
      city: z.string(),
      state: z.string(),
      zip: z.string(),
    }),
  }),
  leadId: z.number(),
  enterpriseId: z.number().nullish(),
  entOwnerId: z.number().nullish(),
  email: z.string(),
  phone: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  leadQualificationPercent: z.number(),
  lightreachProposalNonrequestableReason: z.nativeEnum(LightreachNonrequestableReasonEnum),
  milestoneDisplayName: z.nativeEnum(PreSalesMilestoneNameEnum),
  offer: LeadOfferSchema.optional(),
  offerable: z.boolean(),
  passedStageNames: z.array(z.nativeEnum(StageNameEnum)),
  perWattStrategyId: z.number().nullish(),
  managerId: z.number().nullable(),
  mentorId: z.number().nullable(),
  agentId: z.number(),
  stageId: z.number(),
  milestoneName: z.nativeEnum(PreSalesMilestoneNameEnum),
  stageName: z.nativeEnum(StageNameEnum),
  stageScore: z.number(),
  status: z.object({
    name: z.string().optional(),
    display: z.string().optional(),
    reason: z.string().optional(),
  }),
  projectType: z.nativeEnum(ProjectTypeEnum),
  projectUsers: z.array(ProjectUserSchema),
  proposalNonrequestableReason: z.nativeEnum(ProposalNonrequestableReasonEnum).nullable(),
  proposalUrl: z.string().nullable(),
  qualifyAt: z.string().nullable(),
  closeAt: z.string().nullable(),
  requiresRsm: z.boolean(),
  roofingNonRequestableReason: z.nativeEnum(RoofingNonrequestableReasonEnum).nullable(),
  siteId: z.number(),
  soloProposalNonrequestableReason: z.nativeEnum(SoloNonrequestableReasonEnum).nullable(),
  tickets: z.number(),
  upcomingStageNames: z.array(z.nativeEnum(StageNameEnum)),
  visionProposalNonrequestableReason: z.nativeEnum(VisionNonrequestableReasonEnum).nullable(),
  visionProposal: z
    .object({
      id: z.string(),
      slug: z.string(),
    })
    .nullable(),
});
